import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import CardTypeIcon from './CardTypeIcon';
import {
    keepOnlyDigits,
    formatCreditCardNumber,
    findCardType,
    luhnValidate
} from './utils';
import ErrorLabel from './ErrorLabel';

const CardNumberInput = ({
    readOnly,
    onChange,
    configCardTypes,
    forceDirty = false,
    maskedNumber = null
}) => {
    const { t } = useTranslation();

    const [isValid, setValid] = React.useState(false);
    const [value, setValue] = React.useState('');
    const [isDirty, setDirty] = React.useState(false);
    const [cardType, setCardType] = React.useState(null);
    const [isFocused, setFocused] = React.useState(false);

    React.useEffect(() => {
        if (maskedNumber) {
            const ct = findCardType(maskedNumber, configCardTypes);
            setCardType(ct);
        } else {
            setCardType(null);
        }
    }, [maskedNumber, configCardTypes]);

    return (
        <div
            className={`wz-lyriapay-payline-formblock wz-lyriapay-payline-formblock-cardnumber${
                (isDirty || forceDirty) && !isValid
                    ? ' wz-lyriapay-payline-formblock-error'
                    : ''
            }`}
        >
            <div className="wz-lyriapay-payline-formblock-cardnumber-header">
                <label
                    className="wz-lyriapay-payline-formblock-cardnumber-header-label"
                    htmlFor="cardnumber"
                >
                    {t('cardNumberLabel')}
                </label>
                <div className="wz-lyriapay-payline-formblock-cardnumber-header-icons">
                    <CardTypeIcon
                        configTypes={configCardTypes}
                        type={cardType ? cardType.type : null}
                    />
                </div>
            </div>
            {!maskedNumber && (
                <input
                    id="cardnumber"
                    name="cardnumber"
                    required
                    autoComplete="cc-number"
                    inputMode="numeric"
                    value={value}
                    disabled={readOnly}
                    onBlur={() => setFocused(false)}
                    onFocus={() => setFocused(true)}
                    onChange={e => {
                        const cleanedValue = keepOnlyDigits(e.target.value);
                        const ct = findCardType(cleanedValue, configCardTypes);
                        let valid = Boolean(ct);
                        if (ct && ct.validation === 'luhn') {
                            valid = luhnValidate(cleanedValue);
                        }
                        setDirty(true);
                        setValid(valid);
                        setValue(formatCreditCardNumber(cleanedValue));
                        setCardType(ct);
                        onChange({
                            value: cleanedValue,
                            isValid: valid,
                            cardType: ct
                        });
                    }}
                    aria-invalid={(isDirty || forceDirty) && !isValid && !isFocused}
                    aria-required
                />
            )}
            {maskedNumber && (
                <input
                    value={maskedNumber}
                    disabled={true}
                />
            )}
            <ErrorLabel
                content={
                    (isDirty || forceDirty) && !isValid && !isFocused
                        ? value === ''
                            ? t('cardNumberEmpty')
                            : cardType
                                ? t('cardNumberInvalid')
                                : t('cardNumberUnhandled')
                        : null
                }
            />
        </div>
    );
};

CardNumberInput.propTypes = {
    readOnly: PropTypes.bool.isRequired,
    configCardTypes: PropTypes.array.isRequired,
    onChange: PropTypes.func.isRequired,
    forceDirty: PropTypes.bool,
    maskedNumber: PropTypes.string
};

export default CardNumberInput;
